/**
 * Wellness Settings — Domain API Module
 *
 * Wraps the shared V2 API client for all data operations on the
 * WellnessSettingsPage (Check-in, Rounds, and Careflow tabs).
 *
 * Check-in / Rounds windows share the same DB table:
 *   core_checkin_round_windows  (PK: window_id)
 *
 * Careflow (escalation) data comes from sp_dev_getescalationbycampusid
 * and is written via careflow_* transaction types.
 *
 * Time values are stored in **seconds from midnight** in the database
 * and converted to HH:MM in the UI layer.
 */

import { apiFetch, apiTransaction, apiSave } from '../../../../services/api.js';
import { NURSE_CALL_CATEGORY_FILTER } from '../../../../utils/deviceCategories.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/** Fetch check-in windows for a campus. */
export function fetchCheckinWindows(campusId) {
    return apiFetch({
        procedure: 'sp_dev_getcheckinwindow',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch rounds windows for a campus. */
export function fetchRoundsWindows(campusId) {
    return apiFetch({
        procedure: 'sp_dev_getroundswindow',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch resident groups for a campus. */
export function fetchResidentGroups(campusId) {
    return apiFetch({
        table: 'core_resident_groups',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch area groups for a campus. */
export function fetchAreaGroups(campusId) {
    return apiFetch({
        table: 'core_area_groups',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — Check-in Windows
// ---------------------------------------------------------------------------

/** Add a new check-in window. */
export function addCheckinWindow(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'checkin_add',
        payload,
        campusId,
        userId,
        clientId,
    });
}

/** Edit check-in window(s). */
export function editCheckinWindows(payload, oldPayload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'checkin_edit',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
    });
}

/** Edit check-in window group assignments. */
export function editCheckinGroups(payload, oldPayload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'checkin_edit_groups',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — Rounds Windows
// ---------------------------------------------------------------------------

/** Add a new rounds window. */
export function addRoundsWindow(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'rounds_add',
        payload,
        campusId,
        userId,
        clientId,
    });
}

/** Edit rounds window(s). */
export function editRoundsWindows(payload, oldPayload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'rounds_edit',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
    });
}

/** Edit rounds window group assignments. */
export function editRoundsGroups(payload, oldPayload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'rounds_edit_groups',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — Shared Delete
// ---------------------------------------------------------------------------

/** Delete a single window (used for both check-in and rounds). */
export function deleteWindow(windowId, { campusId, userId, clientId }) {
    return apiSave({
        table: 'core_checkin_round_windows',
        pKey: 'window_id',
        condition: 'primary',
        payload: [{ window_id: windowId }],
        campusId,
        userId,
        clientId,
        method: 'DELETE',
    });
}

/** Delete multiple windows sequentially. */
export async function deleteWindows(windowIds, meta) {
    for (const id of windowIds) {
        const [ok, err] = await deleteWindow(id, meta);
        if (!ok) return [false, err];
    }
    return [true];
}

// ---------------------------------------------------------------------------
// READ operations — Careflow (Escalation)
// ---------------------------------------------------------------------------

/** Fetch all escalation/careflow rows for a campus. */
export function fetchEscalationData(campusId) {
    return apiFetch({
        procedure: 'sp_dev_getescalationbycampusid',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch event categories (alert types). */
export function fetchEventCategories(campusId) {
    return apiFetch({
        table: 'att_event_categories',
        procedure: 'fetchall',
        reqType: 'direct',
        'read-only': true,
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch campus settings (global output flags). */
export function fetchCampusSettings(campusId) {
    return apiFetch({
        table: 'core_campus',
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

/** Fetch Inovonics devices for Device Careflow picker. */
export function fetchDevices(campusId) {
    return apiFetch({
        procedure: 'sp_dev_getInovonicsDevices',
        reqType: 'stored',
        payload: {
            device_filter: '',
            device_category_filter: NURSE_CALL_CATEGORY_FILTER,
            campus_id: campusId,
        },
    });
}

/** Fetch mobile/pager groups for a campus. */
export function fetchMobileGroups(campusId) {
    return apiFetch({
        procedure: 'sp_dev_getgroupsbycampusid',
        reqType: 'stored',
        pKey: 'campus_id',
        pVal: campusId,
    });
}

// ---------------------------------------------------------------------------
// WRITE operations — Careflow
// ---------------------------------------------------------------------------

/** Update campus-level global output flags. */
export function editGlobalOutputs(payload, oldPayload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'careflow_global_edit',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
    });
}

/** Add a new careflow with all children. */
export function addCareflow(payload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'careflow_add',
        payload,
        campusId,
        userId,
        clientId,
    });
}

/** Edit careflow row(s) — parent fields + output toggles. */
export function editCareflow(payload, oldPayload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'careflow_edit',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
    });
}

/** Delete a careflow by careflow_id. */
export function deleteCareflow(careflowId, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'careflow_delete',
        payload: [{ careflow_id: careflowId }],
        campusId,
        userId,
        clientId,
    });
}

/** Delete multiple careflows sequentially. */
export async function deleteCareflows(careflowIds, meta) {
    for (const id of careflowIds) {
        const [ok, err] = await deleteCareflow(id, meta);
        if (!ok) return [false, err];
    }
    return [true];
}

/** Edit a careflow child output (mobile groups, phones, emails, offsite delay). */
export function editCareflowOutput(payload, oldPayload, { campusId, userId, clientId }) {
    return apiTransaction({
        transType: 'careflow_output_edit',
        payload,
        oldPayload,
        campusId,
        userId,
        clientId,
    });
}
